import { useEffect, useRef, useState } from 'react';
import type { GeoBoundary, GeoBoundaryFeatureType, GeoMapPoint } from '../../../lib/metaAdGeoPoints';
import { matchBoundaryForFeature, metersToRadius, radiusToMeters } from '../../../lib/metaAdGeoPoints';
import { MAPS_API_KEY, MAPS_MAP_ID, loadMaps } from '../googleMaps';

// Audience-builder — the interactive map. Two jobs:
//   DISPLAY  — render every geo target, coloured by kind: a marker + radius circle for
//              centre+radius types (custom pins / cities / places / extracted), a marker
//              ONLY for neighborhoods, and AREA OUTLINES for country/region (Google DDS
//              boundaries) + AU postcodes (ABS GeoJSON polygons). Exclusions red.
//   INPUT    — DOUBLE-click empty map to drop a custom-location pin (single click is
//              free); drag a custom pin's marker to MOVE it; drag any editable circle's
//              edge to RESIZE. Cities/places resize but don't move (key encodes centre).
//              Areas have NO radius handle (whole-area targets).
//
// Imperative google.maps (via the shared loader): full control over the
// natively-draggable/editable Circle + the FeatureLayer/Data-layer area drawing, and no
// React-version peer-dep risk. GRACEFUL DEGRADATION: no VITE_GOOGLE_MAPS_API_KEY → a
// notice instead of a map; no VITE_GOOGLE_MAPS_MAP_ID → DDS country/region outlines just
// don't draw (those areas stay chips) while everything else still works.

// Per-kind styling — every kind a DISTINCT colour; any exclusion is red. Legend below
// the map names whichever kinds are present.
type AnyKind = GeoMapPoint['kind'] | GeoBoundary['kind'];
const COLORS: Record<AnyKind | 'excluded', string> = {
  custom: '#7c3aed', // violet — custom pins
  city: '#2563eb', // blue — cities
  place: '#f59e0b', // amber — places
  neighborhood: '#0d9488', // teal — neighborhoods (pin only)
  extracted: '#16a34a', // green — extracted (included)
  country: '#0891b2', // cyan — country outline (DDS)
  region: '#4f46e5', // indigo — region/state outline (DDS)
  zip: '#db2777', // pink — postcode polygon (ABS GeoJSON)
  excluded: '#dc2626', // red — exclusions
};
function colorForPoint(p: GeoMapPoint): string {
  return p.excluded ? COLORS.excluded : COLORS[p.kind];
}
function colorForBoundary(b: GeoBoundary): string {
  return b.excluded ? COLORS.excluded : COLORS[b.kind];
}

// SAFETY — an APPROXIMATE centre (geocoded from the name; may not match what Meta
// publishes) is drawn DE-EMPHASISED so the AM doesn't read it as a confirmed location:
// fainter stroke/fill + a thinner line. Confirmed centres draw solid. The textual warning
// banner below the map carries the explicit caveat.
function circleOpacity(approximate: boolean): { strokeOpacity: number; fillOpacity: number; strokeWeight: number } {
  return approximate
    ? { strokeOpacity: 0.45, fillOpacity: 0.04, strokeWeight: 1 }
    : { strokeOpacity: 0.9, fillOpacity: 0.15, strokeWeight: 2 };
}
function markerTitle(p: GeoMapPoint): string {
  return p.approximate ? `≈ ${p.label} — approximate position, verify before publishing` : p.label;
}

const LEGEND: { kind: AnyKind; label: string }[] = [
  { kind: 'custom', label: 'Custom pins' },
  { kind: 'city', label: 'Cities' },
  { kind: 'place', label: 'Places' },
  { kind: 'neighborhood', label: 'Neighborhoods' },
  { kind: 'country', label: 'Countries' },
  { kind: 'region', label: 'Regions' },
  { kind: 'zip', label: 'Postcodes' },
  { kind: 'extracted', label: 'Extracted' },
];

// The DDS feature-layer types we style (country + admin-area-1). Resolved to the
// google.maps.FeatureType enum at runtime.
const DDS_FEATURE_TYPES: GeoBoundaryFeatureType[] = ['COUNTRY', 'ADMINISTRATIVE_AREA_LEVEL_1'];

// A reasonable fallback centre when there's nothing to fit to yet (~centre of AU).
const FALLBACK_CENTER = { lat: -25.27, lng: 133.78 };

// Pin-drop is a DOUBLE-click (single click is too easy to trigger accidentally).
export const PIN_DROP_EVENT = 'dblclick';
// Map options — disableDoubleClickZoom so a pin-drop double-click doesn't also zoom. A
// vector Map ID (when configured) enables DDS boundary FeatureLayers.
export const MAP_OPTIONS: google.maps.MapOptions = {
  center: FALLBACK_CENTER,
  zoom: 4,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  disableDoubleClickZoom: true,
  ...(MAPS_MAP_ID ? { mapId: MAPS_MAP_ID } : {}),
};

type Overlay = {
  circle: google.maps.Circle | null; // null for marker-only points (neighborhoods)
  marker: google.maps.Marker;
  listeners: google.maps.MapsEventListener[];
};

export function AudienceMap({
  points,
  boundaries = [],
  editable,
  onAddPin,
  onMove,
  onResize,
}: {
  points: GeoMapPoint[];
  boundaries?: GeoBoundary[]; // AREA targets drawn as outlines (country/region/zip)
  editable: boolean; // gates double-click-to-add-pin (per-point move/resize come from the point)
  onAddPin: (latitude: number, longitude: number) => void;
  onMove: (point: GeoMapPoint, latitude: number, longitude: number) => void;
  onResize: (point: GeoMapPoint, radiusInUnit: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const gRef = useRef<typeof google | null>(null); // the resolved google namespace (not the global)
  const overlaysRef = useRef<Map<string, Overlay>>(new Map());
  const suppressRef = useRef(false); // ignore our own change-listeners during prop reconciliation
  const lastFitCountRef = useRef<number>(-1);
  // Boundary drawing state: the GeoJSON Data-layer features we added (to remove on
  // reconcile), and the latest boundaries (read by each FeatureLayer's style fn).
  const dataFeaturesRef = useRef<google.maps.Data.Feature[]>([]);
  const boundariesRef = useRef<GeoBoundary[]>(boundaries);
  boundariesRef.current = boundaries;
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  const cbRef = useRef({ onAddPin, onMove, onResize });
  cbRef.current = { onAddPin, onMove, onResize };
  const pointsRef = useRef(points);
  pointsRef.current = points;
  const editableRef = useRef(editable);
  editableRef.current = editable;

  // ---- init the map once ----
  useEffect(() => {
    if (!MAPS_API_KEY || !containerRef.current) return;
    let cancelled = false;
    setStatus('loading');
    loadMaps()
      .then((g) => {
        if (cancelled || !containerRef.current) return;
        gRef.current = g;
        const map = new g.maps.Map(containerRef.current, MAP_OPTIONS);
        // Pin-drop is a DOUBLE-click (single click left free). disableDoubleClickZoom
        // (MAP_OPTIONS) stops the drop from also zooming.
        map.addListener(PIN_DROP_EVENT, (e: google.maps.MapMouseEvent) => {
          if (!editableRef.current || !e.latLng) return;
          cbRef.current.onAddPin(e.latLng.lat(), e.latLng.lng());
        });
        mapRef.current = map;
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- reconcile overlays whenever the points change ----
  useEffect(() => {
    const map = mapRef.current;
    const g = gRef.current;
    if (status !== 'ready' || !map || !g) return;
    const overlays = overlaysRef.current;
    const seen = new Set<string>();

    suppressRef.current = true;
    for (const p of points) {
      seen.add(p.id);
      const radiusM = radiusToMeters(p.radius, p.distanceUnit);
      const color = colorForPoint(p);
      const op = circleOpacity(p.approximate);
      let ov = overlays.get(p.id);
      if (!ov) {
        const circle = p.drawCircle
          ? new g.maps.Circle({
              map,
              center: { lat: p.latitude, lng: p.longitude },
              radius: radiusM,
              strokeColor: color,
              strokeOpacity: op.strokeOpacity,
              strokeWeight: op.strokeWeight,
              fillColor: color,
              fillOpacity: op.fillOpacity,
              editable: p.editableRadius,
              draggable: false, // the MARKER moves the centre; the circle only resizes
              clickable: false,
            })
          : null;
        const marker = new g.maps.Marker({
          map,
          position: { lat: p.latitude, lng: p.longitude },
          title: markerTitle(p),
          draggable: p.draggable,
          opacity: p.approximate ? 0.6 : 1,
        });
        const listeners: google.maps.MapsEventListener[] = [];
        if (circle && p.editableRadius) {
          listeners.push(
            circle.addListener('radius_changed', () => {
              if (suppressRef.current) return;
              const cur = pointsRef.current.find((x) => x.id === p.id);
              if (!cur) return;
              cbRef.current.onResize(cur, metersToRadius(circle.getRadius(), cur.distanceUnit));
            }),
          );
        }
        if (p.draggable) {
          listeners.push(
            marker.addListener('dragend', () => {
              if (suppressRef.current) return;
              const pos = marker.getPosition();
              const cur = pointsRef.current.find((x) => x.id === p.id);
              if (!cur || !pos) return;
              circle?.setCenter(pos);
              cbRef.current.onMove(cur, pos.lat(), pos.lng());
            }),
          );
        }
        ov = { circle, marker, listeners };
        overlays.set(p.id, ov);
      } else {
        // Update geometry/style in place (no destroy → an in-flight drag survives).
        if (ov.circle) {
          ov.circle.setCenter({ lat: p.latitude, lng: p.longitude });
          if (Math.abs(ov.circle.getRadius() - radiusM) > 0.5) ov.circle.setRadius(radiusM);
          ov.circle.setEditable(p.editableRadius);
          ov.circle.setOptions({
            strokeColor: color,
            fillColor: color,
            strokeOpacity: op.strokeOpacity,
            strokeWeight: op.strokeWeight,
            fillOpacity: op.fillOpacity,
          });
        }
        ov.marker.setPosition({ lat: p.latitude, lng: p.longitude });
        ov.marker.setDraggable(p.draggable);
        ov.marker.setTitle(markerTitle(p));
        ov.marker.setOpacity(p.approximate ? 0.6 : 1);
      }
    }

    // Remove overlays whose point is gone.
    for (const [id, ov] of overlays) {
      if (seen.has(id)) continue;
      ov.listeners.forEach((l) => l.remove());
      ov.circle?.setMap(null);
      ov.marker.setMap(null);
      overlays.delete(id);
    }
    suppressRef.current = false;

    // Fit bounds only when the SET of points changes (not on every radius tweak).
    if (points.length !== lastFitCountRef.current) {
      lastFitCountRef.current = points.length;
      if (points.length > 0) {
        const bounds = new g.maps.LatLngBounds();
        for (const p of points) {
          const b = overlays.get(p.id)?.circle?.getBounds();
          if (b) bounds.union(b);
          else bounds.extend({ lat: p.latitude, lng: p.longitude });
        }
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, 48);
          if (points.length === 1 && map.getZoom() && map.getZoom()! > 13) map.setZoom(13);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listeners read live state via refs
  }, [points, status]);

  // ---- reconcile AREA boundaries (DDS country/region + GeoJSON postcodes) ----
  useEffect(() => {
    const map = mapRef.current;
    const g = gRef.current;
    if (status !== 'ready' || !map || !g) return;

    // (1) DDS country/region — style the matching FeatureLayers by place id. Needs a
    // vector Map ID + the feature layers enabled on the map style; guarded so an
    // unconfigured map (no Map ID) just skips (those areas stay chips).
    if (MAPS_MAP_ID) {
      for (const ft of DDS_FEATURE_TYPES) {
        const enumType = (g.maps.FeatureType as Record<string, google.maps.FeatureType>)[ft];
        if (!enumType) continue;
        try {
          const layer = map.getFeatureLayer(enumType);
          // The style fn reads the LIVE boundaries via the ref. The boundary FeatureLayer
          // hands a PlaceFeature (it carries `placeId`).
          layer.style = (params: google.maps.FeatureStyleFunctionOptions) => {
            const placeId = (params.feature as google.maps.PlaceFeature).placeId;
            const b = matchBoundaryForFeature(boundariesRef.current, ft, placeId);
            if (!b) return null;
            const color = colorForBoundary(b);
            // Approximate (name-geocoded) areas draw fainter — see circleOpacity.
            return b.approximate
              ? { fillColor: color, fillOpacity: 0.05, strokeColor: color, strokeWeight: 1, strokeOpacity: 0.45 }
              : { fillColor: color, fillOpacity: 0.18, strokeColor: color, strokeWeight: 2, strokeOpacity: 0.9 };
          };
        } catch {
          /* layer not enabled on this map style — skip (graceful) */
        }
      }
    }

    // (2) GeoJSON postcodes — replace the Data-layer features with the current set.
    for (const f of dataFeaturesRef.current) map.data.remove(f);
    dataFeaturesRef.current = [];
    map.data.setStyle((feature: google.maps.Data.Feature) => {
      const color = (feature.getProperty('kyckColor') as string) || COLORS.zip;
      return { fillColor: color, fillOpacity: 0.18, strokeColor: color, strokeWeight: 2, strokeOpacity: 0.9 };
    });
    for (const b of boundaries) {
      if (!b.geojson) continue;
      try {
        const added = map.data.addGeoJson(b.geojson as object);
        for (const f of added) {
          f.setProperty('kyckColor', colorForBoundary(b));
          dataFeaturesRef.current.push(f);
        }
      } catch {
        /* malformed geojson — skip (graceful) */
      }
    }

    // (3) Fit to area bounds (DDS viewport + postcode polygon extents) when the set
    // changes, so a newly-added area is brought into view.
    const fitBox = new g.maps.LatLngBounds();
    let any = false;
    for (const b of boundaries) {
      if (b.bounds) {
        fitBox.extend({ lat: b.bounds.north, lng: b.bounds.east });
        fitBox.extend({ lat: b.bounds.south, lng: b.bounds.west });
        any = true;
      }
    }
    for (const f of dataFeaturesRef.current) {
      f.getGeometry()?.forEachLatLng((ll) => {
        fitBox.extend(ll);
        any = true;
      });
    }
    if (any && !fitBox.isEmpty()) map.fitBounds(fitBox, 48);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- style fns read live boundaries via the ref
  }, [boundaries, status]);

  if (!MAPS_API_KEY) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-xs text-slate-500">
        Map unavailable — set <code className="font-mono">VITE_GOOGLE_MAPS_API_KEY</code> to enable the
        interactive audience map. Cities, places and custom locations can still be reviewed in the lists/chips.
      </div>
    );
  }

  const presentKinds = new Set<AnyKind>([...points.map((p) => p.kind), ...boundaries.map((b) => b.kind)]);
  const hasExclusions = points.some((p) => p.excluded) || boundaries.some((b) => b.excluded);
  // SAFETY — any geocoded-by-name location on the map may not match what Meta publishes
  // (the key is authoritative; the map position is a best-guess from the name). Warn
  // explicitly so an AM never trusts a drawn position over the actual targeted place.
  const hasApproximate = points.some((p) => p.approximate) || boundaries.some((b) => b.approximate);

  return (
    <div className="space-y-1.5">
      {hasApproximate && (
        <div
          role="alert"
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
        >
          <span className="font-semibold">Approximate positions.</span> Locations you searched (cities, suburbs,
          states) are shown at their best-guess position <span className="font-semibold">by name</span> and may be a
          different area — or even a different country — than the one Meta will actually target. The faded
          circles/outlines marked “≈” are unconfirmed. <span className="font-semibold">Verify each location in Meta
          Ads Manager before publishing.</span>
        </div>
      )}
      <div
        ref={containerRef}
        className="h-80 w-full overflow-hidden rounded-md border border-slate-200 bg-slate-100"
        aria-label="Audience targeting map"
      />
      {status === 'error' && (
        <p className="text-xs text-red-600">Couldn’t load the map. Check the Maps API key / network.</p>
      )}
      {(presentKinds.size > 0 || hasExclusions) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          {LEGEND.filter((l) => presentKinds.has(l.kind)).map((l) => (
            <span key={l.kind} className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS[l.kind] }} />
              {l.label}
            </span>
          ))}
          {hasExclusions && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS.excluded }} />
              Excluded
            </span>
          )}
        </div>
      )}
      {status === 'ready' && editable && (
        <p className="text-xs text-slate-400">
          Double-click the map to drop a pin. Drag a pin to move it; drag a circle’s edge to resize the radius.
        </p>
      )}
    </div>
  );
}
